import React, {useEffect, useState} from "react";
import axios from "../../../api/axiosApi";
import {getStoredToken} from "../../../utils";
import {map} from "lodash";
import "./view-statement.css";

const amount = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});
};

const MOBILE_QUERY = "(max-width: 768px)";

// Without props: the logged-in payer's current statement (fetched from new-api).
// With `statement`: render that statement as-is (admin dashboard) — no fetch, no payer token needed.
export const ViewStatement = ({statement: providedStatement} = {}) => {

    const [isLoading, setIsLoading] = useState(!providedStatement);
    const [statement, setStatement] = useState(providedStatement || null);
    const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
    const [showPaperOnMobile, setShowPaperOnMobile] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia(MOBILE_QUERY);
        const onChange = (event) => setIsMobile(event.matches);
        if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", onChange);
        else mediaQuery.addListener(onChange);
        return () => {
            if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", onChange);
            else mediaQuery.removeListener(onChange);
        };
    }, []);

    const loadStatement = async () => {
        setIsLoading(true);
        try {
            const result = await axios.get("statements/current", {
                headers: {
                    "micro-service": "appointment",
                    "Authorization": "Bearer " + getStoredToken(),
                },
            });
            setStatement(result.data || null);
        } catch (e) {
            setStatement(null);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (providedStatement) {
            setStatement(providedStatement);
            setIsLoading(false);
            return;
        }
        loadStatement();
    }, [providedStatement]);

    if (isLoading) {
        return (
            <div className="homeexisting-patient-box account-card position-relative account-balance-card m-auto mt-5 mb-5">
                <div className="hommeexisting-large-text hommeexisting-hoiz-text">Statement</div>
                <div className="when-you-have-an-amo no-amount">Loading your statement...</div>
            </div>
        );
    }

    if (!statement) {
        return (
            <div className="homeexisting-patient-box account-card position-relative account-balance-card m-auto mt-5 mb-5">
                <div className="hommeexisting-large-text hommeexisting-hoiz-text">Statement</div>
                <div className="when-you-have-an-amo no-amount">No statement is available at this time.</div>
            </div>
        );
    }

    const header = statement.header || {};
    const footer = statement.footer || {};
    const details = statement.details || [];

    const practiceLines = [header.practiceLine1, header.practiceLine2, header.practiceLine3,
        header.practiceLine4, header.practiceLine5].filter(Boolean);
    const respPartyLines = [header.respPartyLine1, header.respPartyLine2, header.respPartyLine3,
        header.respPartyLine4].filter(Boolean);

    const showMobileCards = isMobile && !showPaperOnMobile;

    const mobileAmounts = (line) => [
        {label: "Charge", value: line.charge},
        {label: "Payment", value: line.paid},
        {label: "Adjust", value: line.adjust},
    ].filter(a => amount(a.value) !== "");

    const balanceRows = [
        {label: "Previous Balance", value: footer.prevBalance},
        {label: "New Charges", value: footer.newCharges},
        {label: "New Payments", value: footer.newPaids},
        {label: "Other Credits", value: footer.newCredits},
        {label: "New Balance", value: footer.newBalance},
        {label: "Amt Due from Insurance", value: footer.dueFromInsurance},
    ];

    const agingRows = [
        {label: "Current", value: footer.agingCurrent},
        {label: "Over 30 Days", value: footer.agingOver30},
        {label: "Over 60 Days", value: footer.agingOver60},
        {label: "Over 90 Days", value: footer.agingOver90},
        {label: "Over 120 Days", value: footer.agingOver120},
        {label: "Unapplied Credits", value: footer.unappliedCredits},
    ];

    return (
        <div className="homeexisting-patient-box account-card position-relative statement-card m-auto mt-5 mb-5">
            <div className="hommeexisting-large-text hommeexisting-hoiz-text">Statement</div>

            <div className="statement-print-row">
                {isMobile && (
                    <React.Fragment>
                        <button type="button"
                                className={"statement-print-button" + (showMobileCards ? " statement-button-active" : "")}
                                onClick={() => setShowPaperOnMobile(false)}>
                            View Statement
                        </button>
                        <button type="button"
                                className={"statement-print-button" + (!showMobileCards ? " statement-button-active" : "")}
                                onClick={() => setShowPaperOnMobile(true)}>
                            View PDF
                        </button>
                    </React.Fragment>
                )}
                <button type="button" className="statement-print-button" onClick={() => window.print()}>
                    {isMobile ? "Print PDF" : "Print Statement"}
                </button>
            </div>

            {showMobileCards && (
                <div className="statement-mobile">
                    <div className="statement-mobile-block statement-mobile-practice">
                        {map(practiceLines, (line, index) => <div key={index}>{line}</div>)}
                    </div>
                    <div className="statement-mobile-block">
                        <div className="statement-mobile-row"><span>Statement Date</span><span>{header.StatementDate}</span></div>
                        <div className="statement-mobile-row"><span>Account Number</span><span>{header.patientAcctID}</span></div>
                        <div className="statement-mobile-row"><span>Patient Name</span><span>{header.patientName}</span></div>
                    </div>

                    <div className="statement-mobile-section-title">Activity</div>
                    {map(details, (line, index) => {
                        const isGroupRow = (line.description || "").trim().startsWith("----");
                        if (isGroupRow) {
                            return (
                                <div key={index} className="statement-mobile-group">
                                    {(line.description || "").replace(/^-+\s*/, "")}
                                </div>
                            );
                        }
                        return (
                            <div key={index} className="statement-mobile-line">
                                <div className="statement-mobile-line-top">
                                    <span className="statement-mobile-date">{line.date}</span>
                                    <span className="statement-mobile-amounts">
                                        {map(mobileAmounts(line), (a) => (
                                            <span key={a.label} className="statement-mobile-amount">
                                                <span className="statement-mobile-amount-label">{a.label}</span> {amount(a.value)}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                                <div className="statement-mobile-desc">{line.description}</div>
                            </div>
                        );
                    })}

                    <div className="statement-mobile-section-title">Balance</div>
                    <div className="statement-mobile-block">
                        {map(balanceRows, (row) => (
                            <div key={row.label} className="statement-mobile-row">
                                <span>{row.label}</span><span>{amount(row.value)}</span>
                            </div>
                        ))}
                        <div className="statement-mobile-row statement-mobile-pay-row">
                            <span>PAY THIS AMOUNT</span><span>{amount(footer.pleasePay)}</span>
                        </div>
                    </div>

                    {footer.agingVisible && (
                        <React.Fragment>
                            <div className="statement-mobile-section-title">Aging</div>
                            <div className="statement-mobile-block">
                                {map(agingRows, (row) => (
                                    <div key={row.label} className="statement-mobile-row">
                                        <span>{row.label}</span><span>{amount(row.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </React.Fragment>
                    )}

                    {(footer.dunningMessage || footer.billMessage || footer.bottomMessage1 || footer.bottomMessage2) && (
                        <div className="statement-mobile-messages">
                            {footer.dunningMessage && <div>{footer.dunningMessage}</div>}
                            {footer.billMessage && <div>{footer.billMessage}</div>}
                            {footer.bottomMessage1 && <div>{footer.bottomMessage1}</div>}
                            {footer.bottomMessage2 && <div>{footer.bottomMessage2}</div>}
                        </div>
                    )}
                </div>
            )}

            <div className={"statement-paper" + (showMobileCards ? " statement-paper--print-only" : "")}>

                {/* HEADER */}
                <div className="statement-header-row">
                    <div className="statement-address-block">
                        {map(practiceLines, (line, index) => <div key={index}>{line}</div>)}
                    </div>
                </div>
                <div className="statement-header-row">
                    <div className="statement-address-block">
                        {map(respPartyLines, (line, index) => <div key={index}>{line}</div>)}
                    </div>
                    <div className="statement-meta-block">
                        <div><span>Statement Date:</span> {header.StatementDate}</div>
                        <div><span>Account Number:</span> {header.patientAcctID}</div>
                        <div><span>Patient Name:</span> {header.patientName}</div>
                    </div>
                </div>

                {/* hard-coded banner (not part of the XML) */}
                <div className="statement-banner">
                    <div className="statement-banner-title">STATEMENT FOR PROFESSIONAL SERVICES</div>
                    <div className="statement-banner-subtitle">
                        CHARGES OR PAYMENTS AFTER ABOVE STATEMENT DATE WILL APPEAR ON YOUR NEXT STATEMENT
                    </div>
                </div>

                {/* DETAILS */}
                <div className="statement-table-scroll">
                    <table className="statement-table statement-details-table">
                        <thead>
                        <tr>
                            <th className="statement-col-date">DATE</th>
                            <th>DESCRIPTION</th>
                            <th className="statement-col-amount">CHARGES</th>
                            <th className="statement-col-amount">PAYMENTS</th>
                            <th className="statement-col-amount">ADJUSTS</th>
                        </tr>
                        </thead>
                        <tbody>
                        {map(details, (line, index) => {
                            const isGroupRow = (line.description || "").trim().startsWith("----");
                            return (
                                <tr key={index}>
                                    <td className="statement-col-date">{line.date}</td>
                                    <td className={isGroupRow ? "statement-group-row" : ""}>{line.description}</td>
                                    <td className="statement-col-amount">{amount(line.charge)}</td>
                                    <td className="statement-col-amount">{amount(line.paid)}</td>
                                    <td className="statement-col-amount">{amount(line.adjust)}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {/* dunning / bill messages (from the XML footer, shown under the details box) */}
                {(footer.dunningMessage || footer.billMessage) && (
                    <div className="statement-messages">
                        {footer.dunningMessage && <div>{footer.dunningMessage}</div>}
                        {footer.billMessage && <div>{footer.billMessage}</div>}
                    </div>
                )}

                {/* FOOTER — balances */}
                <div className="statement-table-scroll">
                    <table className="statement-table statement-footer-table">
                        <thead>
                        <tr>
                            <th>Previous Balance</th>
                            <th>New Charges</th>
                            <th>New Payments</th>
                            <th>Other Credits</th>
                            <th>New Balance</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>{amount(footer.prevBalance)}</td>
                            <td>{amount(footer.newCharges)}</td>
                            <td>{amount(footer.newPaids)}</td>
                            <td>{amount(footer.newCredits)}</td>
                            <td>{amount(footer.newBalance)}</td>
                        </tr>
                        <tr>
                            <td className="statement-cell-empty"/>
                            <th>Amt Due from Ins --&gt;</th>
                            <td>{amount(footer.dueFromInsurance)}</td>
                            <th>PAY THIS AMT --&gt;</th>
                            <td className="statement-pay-amount">{amount(footer.pleasePay)}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                {/* FOOTER — aging (only when the practice wants it shown) */}
                {footer.agingVisible && (
                    <div className="statement-table-scroll">
                        <table className="statement-table statement-footer-table">
                            <thead>
                            <tr>
                                <th>CURRENT</th>
                                <th>Over 30 Days</th>
                                <th>Over 60 Days</th>
                                <th>Over 90 Days</th>
                                <th>Over 120 Days</th>
                                <th>Unapplied Credits</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>{amount(footer.agingCurrent)}</td>
                                <td>{amount(footer.agingOver30)}</td>
                                <td>{amount(footer.agingOver60)}</td>
                                <td>{amount(footer.agingOver90)}</td>
                                <td>{amount(footer.agingOver120)}</td>
                                <td>{amount(footer.unappliedCredits)}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {(footer.bottomMessage1 || footer.bottomMessage2) && (
                    <div className="statement-bottom-messages">
                        {footer.bottomMessage1 && <div>{footer.bottomMessage1}</div>}
                        {footer.bottomMessage2 && <div>{footer.bottomMessage2}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};
